import { Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExplorerHeader } from "@/features/explorer/components/ExplorerHeader";

export function GlobalErrorComponent({ error }: { error: unknown }) {
  const router = useRouter();

  return (
    <div className='fixed inset-0 z-40 flex w-full flex-col overflow-hidden bg-background font-sans text-foreground'>
      <ExplorerHeader />

      {/* Ambient background glow accents */}
      <div className='pointer-events-none absolute top-1/4 left-1/2 size-96 -translate-x-1/2 rounded-full bg-destructive/10 blur-3xl' />
      <div className='pointer-events-none absolute right-1/4 bottom-1/4 size-64 rounded-full bg-destructive/5 blur-3xl' />

      <main className='relative z-10 flex flex-1 items-center justify-center p-6'>
        <div className='w-full max-w-md space-y-6 text-center'>
          <div className='mx-auto flex size-24 rotate-6 items-center justify-center rounded-3xl border border-destructive/25 bg-destructive/10 text-destructive shadow-inner backdrop-blur-md'>
            <AlertTriangle className='size-12 -rotate-6' />
          </div>

          <div className='space-y-3'>
            <Badge
              variant='secondary'
              className='rounded-md border border-destructive/30 bg-destructive/10 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-destructive'>
              Error
            </Badge>
            <h1 className='text-3xl font-extrabold tracking-tight sm:text-4xl'>Something went wrong</h1>
            <p className='leading-relaxed text-muted-foreground'>
              An unexpected error occurred while processing your data. We've been notified and are working on it.
            </p>
          </div>

          {import.meta.env.DEV && (
            <pre className='max-h-40 overflow-auto rounded-lg border border-border bg-muted p-4 text-left font-mono text-xs'>
              {error instanceof Error ? error.message : String(error)}
            </pre>
          )}

          <div className='flex flex-col items-center gap-3 pt-2 sm:flex-row'>
            <Button
              variant='outline'
              className='h-12 w-full gap-2 sm:w-44'
              onClick={() => {
                void router.invalidate();
              }}>
              <RotateCcw className='size-4' />
              Try Again
            </Button>
            <Link to='/' className='w-full sm:w-auto'>
              <Button className='h-12 w-full gap-2 sm:w-44'>
                <Home className='size-4' />
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
